import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  DataService,
  UserMigrationBatchStatus,
  WorkflowContextService
} from 'src/app/services';
import { Document, Client } from 'src/app/models';
import {
  UploadComponent,
  UploadResponse
} from 'src/app/components/upload/upload.component';

interface UserCredential {
  username: string;
  password: string;
}

@Component({
  selector: 'wm-user-migration',
  templateUrl: './user-migration.component.html',
  styleUrls: ['./user-migration.component.css'],
  standalone: false
})
export class UserMigrationComponent implements OnInit, OnDestroy {
  @ViewChild('userMigrationUploadComponent', { static: false })
  userMigrationUploadComponent!: UploadComponent;

  uploadKey: string = '';
  userMigrationFilePath: string | null = null;
  userMigrationUploaded: boolean = false;
  selectedClientId: string | null = null;
  clients: Client[] = [];
  loadingClients: boolean = false;

  loading: boolean = false;
  error: string | null = null;
  success: boolean = false;
  migrationInProgress: boolean = false;

  generatedCredentials: UserCredential[] = [];
  showCredentials: boolean = false;
  migrationBatchId: string | null = null;
  scheduledTaskQueueId: string | null = null;
  migrationStatus: string | null = null;
  migrationTaskError: string | null = null;
  usernameConflictCount: number = 0;
  private taskStatusPollTimer: number | null = null;
  activeTab: 'new' | 'batches' = 'new';
  migrationBatches: UserMigrationBatchStatus[] = [];
  loadingBatches: boolean = false;
  private batchesPollTimer: number | null = null;

  constructor(
    private _router: Router,
    private _dataSvc: DataService,
    private _context: WorkflowContextService
  ) {}

  ngOnInit() {
    this.uploadKey = this.generateId();
    this.loadClients();
  }

  ngOnDestroy() {
    this.stopTaskStatusPolling();
    this.stopBatchesPolling();
  }

  selectTab(tab: 'new' | 'batches') {
    this.activeTab = tab;

    if (tab === 'batches') {
      this.loadMigrationBatches();
    } else {
      this.stopBatchesPolling();
    }
  }

  loadClients() {
    this.loadingClients = true;
    this._dataSvc.getClients().subscribe({
      next: clients => {
        this.clients = clients.sort((a, b) => a.name.localeCompare(b.name));
        this.loadingClients = false;
      },
      error: err => {
        this.error =
          'Failed to load jurisdictions: ' +
          (err.error?.message || err.message);
        this.loadingClients = false;
      }
    });
  }

  onUserMigrationFileUploaded(response: UploadResponse) {
    this.userMigrationFilePath = response.path;
    this.userMigrationUploaded = true;
    this.error = null;
    this.showCredentials = false;
    this.generatedCredentials = [];
    this.migrationBatchId = null;
    this.scheduledTaskQueueId = null;
    this.migrationStatus = null;
    this.migrationTaskError = null;
    this.usernameConflictCount = 0;
  }

  onUserMigrationFileDeleted(document: Document) {
    this.userMigrationFilePath = null;
    this.userMigrationUploaded = false;
    this.showCredentials = false;
    this.generatedCredentials = [];
    this.migrationBatchId = null;
    this.scheduledTaskQueueId = null;
    this.migrationStatus = null;
    this.migrationTaskError = null;
    this.usernameConflictCount = 0;
  }

  migrateUsers() {
    // Validate required fields
    if (!this.userMigrationUploaded) {
      this.error = 'Please upload a user migration file';
      return;
    }

    if (!this.selectedClientId) {
      this.error = 'Please select a jurisdiction';
      return;
    }

    this.loading = true;
    this.migrationInProgress = true;
    this.error = null;
    this.success = false;
    this.showCredentials = false;
    this.generatedCredentials = [];

    // Call the user migration API with clientId
    this._dataSvc
      .migrateUsers(this.userMigrationFilePath!, this.selectedClientId)
      .subscribe({
        next: response => {
          this.loading = false;
          this.migrationInProgress = false;
          this.success = true;

          if (response) {
            this.migrationBatchId = response.batchId;
            this.scheduledTaskQueueId = response.scheduledTaskQueueId;
            this.migrationStatus = response.status;
            this.migrationTaskError = null;
            this.usernameConflictCount = response.usernameConflictCount || 0;
            const credentials = response.generatedCredentials || {};
            this.generatedCredentials = Object.keys(credentials).map(username => ({
              username: username,
              password: credentials[username]
            }));

            this.showCredentials = true;
            this.pollTaskStatus();
          }
        },
        error: err => {
          this.loading = false;
          this.migrationInProgress = false;
          this.error =
            'User migration failed: ' + (err.error?.message || err.message);
        }
      });
  }

  pollTaskStatus() {
    if (!this.migrationBatchId || !this.scheduledTaskQueueId) {
      return;
    }

    this.stopTaskStatusPolling();
    this._dataSvc
      .getUserMigrationTaskStatus(
        this.migrationBatchId,
        this.scheduledTaskQueueId
      )
      .subscribe({
        next: status => {
          this.migrationStatus = status.status;
          this.migrationTaskError = status.errorMessage || null;
          this.usernameConflictCount = status.usernameConflictCount || 0;

          if (status.status === 'Queued' || status.status === 'Processing') {
            this.taskStatusPollTimer = window.setTimeout(
              () => this.pollTaskStatus(),
              5000
            );
          }
        },
        error: err => {
          this.migrationTaskError =
            'Unable to refresh task status: ' +
            (err.error?.message || err.message);
          this.taskStatusPollTimer = window.setTimeout(
            () => this.pollTaskStatus(),
            5000
          );
        }
      });
  }

  private stopTaskStatusPolling() {
    if (this.taskStatusPollTimer !== null) {
      window.clearTimeout(this.taskStatusPollTimer);
      this.taskStatusPollTimer = null;
    }
  }

  loadMigrationBatches() {
    this.stopBatchesPolling();
    this.loadingBatches = true;
    this._dataSvc.getUserMigrationBatches().subscribe({
      next: batches => {
        this.migrationBatches = batches || [];
        this.loadingBatches = false;
        this.scheduleBatchesPoll();
      },
      error: err => {
        this.loadingBatches = false;
        this.error =
          'Failed to load user migration batches: ' +
          (err.error?.message || err.message);
      }
    });
  }

  private scheduleBatchesPoll() {
    if (this.activeTab !== 'batches' || !this.getIncompleteBatchIds().length) {
      return;
    }

    this.batchesPollTimer = window.setTimeout(
      () => this.pollIncompleteBatches(),
      5000
    );
  }

  private pollIncompleteBatches() {
    const incompleteBatchIds = this.getIncompleteBatchIds();

    if (!incompleteBatchIds.length) {
      return;
    }

    this._dataSvc.getUserMigrationBatchStatuses(incompleteBatchIds).subscribe({
      next: statuses => {
        const statusByBatchId = new Map(
          (statuses || []).map(status => [status.batchId, status])
        );

        this.migrationBatches.forEach(batch => {
          const updatedStatus = statusByBatchId.get(batch.batchId);

          if (updatedStatus) {
            batch.status = updatedStatus.status;
            batch.errorMessage = updatedStatus.errorMessage;
            batch.usernameConflictCount = updatedStatus.usernameConflictCount;
            batch.scheduledTaskQueueId = updatedStatus.scheduledTaskQueueId;
          }
        });

        this.scheduleBatchesPoll();
      },
      error: err => {
        this.error =
          'Failed to refresh user migration batch status: ' +
          (err.error?.message || err.message);
        this.scheduleBatchesPoll();
      }
    });
  }

  private getIncompleteBatchIds(): string[] {
    return this.migrationBatches
      .filter(batch => batch.status !== 'Completed' && batch.status !== 'Error')
      .map(batch => batch.batchId);
  }

  private stopBatchesPolling() {
    if (this.batchesPollTimer !== null) {
      window.clearTimeout(this.batchesPollTimer);
      this.batchesPollTimer = null;
    }
  }

  exportCredentials() {
    if (this.generatedCredentials.length === 0) {
      return;
    }

    // Create CSV content
    let csvContent = 'Username,Password\n';

    this.generatedCredentials.forEach(cred => {
      csvContent += `${cred.username},${cred.password}\n`;
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `user-credentials-${new Date().getTime()}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  downloadUsernameConflicts(batchId: string = this.migrationBatchId) {
    if (!batchId) {
      return;
    }

    this._dataSvc
      .downloadUserMigrationUsernameConflicts(batchId)
      .subscribe({
        next: data => {
          const url = URL.createObjectURL(data);
          const link = document.createElement('a');
          link.href = url;
          link.download = `user-migration-conflicts-${batchId}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        },
        error: async err => {
          this.error =
            'Failed to download username conflicts: ' +
            (await this.getDownloadErrorMessage(err));
        }
      });
  }

  downloadMigrationFile(batch: UserMigrationBatchStatus) {
    this._dataSvc
      .downloadUserMigrationFile(batch.batchId)
      .subscribe({
        next: data => {
          const url = URL.createObjectURL(data);
          const link = document.createElement('a');
          link.href = url;
          link.download = this.getMigrationFileName(batch.userMigrationFilePath);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        },
        error: async err => {
          this.error =
            'Failed to download user migration file: ' +
            (await this.getDownloadErrorMessage(err));
        }
      });
  }

  private getMigrationFileName(filePath: string): string {
    const segments = (filePath || '').split(/[\\/]/);
    return segments[segments.length - 1] || 'user-migration.json';
  }

  private async getDownloadErrorMessage(error: any): Promise<string> {
    if (!(error?.error instanceof Blob)) {
      return error?.error?.message || error?.message || 'Unknown error';
    }

    try {
      const response = JSON.parse(await error.error.text());
      return response.message || response.Message || error.message || 'Unknown error';
    } catch {
      return error.message || 'Unknown error';
    }
  }

  copyCredentialsToClipboard() {
    if (this.generatedCredentials.length === 0) {
      return;
    }

    let textContent = 'User Credentials\n\n';

    this.generatedCredentials.forEach(cred => {
      textContent += `Username: ${cred.username}\nPassword: ${cred.password}\n\n`;
    });

    navigator.clipboard.writeText(textContent).then(
      () => {
        alert('Credentials copied to clipboard!');
      },
      err => {
        console.error('Failed to copy credentials: ', err);
        alert('Failed to copy credentials to clipboard.');
      }
    );
  }

  reset() {
    this.stopTaskStatusPolling();
    this.userMigrationFilePath = null;
    this.userMigrationUploaded = false;
    this.showCredentials = false;
    this.generatedCredentials = [];
    this.migrationBatchId = null;
    this.scheduledTaskQueueId = null;
    this.migrationStatus = null;
    this.migrationTaskError = null;
    this.usernameConflictCount = 0;
    this.success = false;
    this.error = null;

    if (this.userMigrationUploadComponent) {
      // Clear the upload component if needed
      this.uploadKey = this.generateId();
    }
  }

  cancel() {
    this._router.navigate(['/admin/global/users']);
  }

  private generateId(): string {
    return (
      Math.random()
        .toString(36)
        .substring(2, 15) +
      Math.random()
        .toString(36)
        .substring(2, 15)
    );
  }
}
